import React from 'react';
import copy from 'copy-to-clipboard';
import { toast } from 'react-toastify';
import {
	AiConversationMessage,
	buildAiMessageLink,
	canShareAiMessageLink,
	formatMessageTime,
	getAiMessageErrorText,
	isAiMessageError
} from '../interface/ai';
import AiMessageContent from './aiMessageContent';

interface Props {
	message: AiConversationMessage;
	hasErrorResponse?: boolean;
	isHighlighted?: boolean;
}

const AiMessageItem: React.FC<Props> = ({ message, hasErrorResponse = false, isHighlighted = false }) => {
	const isAdmin = message.role === 'admin';
	const isError = isAiMessageError(message);
	const hasTable = !isAdmin && !isError && message.content.includes('|');
	const canCopyLink = canShareAiMessageLink(message);

	const handleCopyLink = () => {
		const link = buildAiMessageLink(message.conversationId, message.messageId);
		if (copy(link)) {
			toast.success('Message link copied to clipboard.');
			return;
		}
		toast.error('Failed to copy link.');
	};

	return (
		<div
			className={`telegram-message-card ${isAdmin ? 'is-outgoing' : 'is-incoming'} ${
				hasTable ? 'ai-message-card--rich' : ''
			} ${isError ? 'ai-message-card--error' : ''} ${hasErrorResponse ? 'ai-message-card--prompt-error' : ''} ${
				isHighlighted ? 'is-highlighted' : ''
			}`}
			data-ai-message-id={message.messageId}
		>
			<span className={`telegram-message-card__avatar ${isAdmin ? '' : 'is-bot'} ${isError ? 'is-error' : ''}`}>
				<i className={`fa ${isAdmin ? 'fa-user' : isError ? 'fa-exclamation-triangle' : 'fa-magic'}`} />
			</span>
			<div className={`telegram-message-card__bubble ${isError ? 'is-error' : ''}`}>
				<div className="telegram-message-card__header">
					<span className="telegram-message-card__sender">
						{isAdmin ? 'You' : isError ? 'Claude Error' : 'Claude'}
					</span>
					<div className="telegram-message-card__meta">
						{canCopyLink ? (
							<button
								type="button"
								className="telegram-message-card__reply-btn"
								onClick={handleCopyLink}
								title="Copy link to this message"
								aria-label="Copy link to this message"
							>
								<i className="fa fa-link" />
							</button>
						) : null}
						<span className="telegram-message-card__time">{formatMessageTime(message.createdAt)}</span>
					</div>
				</div>
				{hasErrorResponse ? (
					<div className="ai-message__prompt-error-badge">
						<i className="fa fa-exclamation-circle" />
						<span>Request failed</span>
					</div>
				) : null}
				{isError ? (
					<div className="ai-message__error">
						<div className="ai-message__error-title">
							<i className="fa fa-exclamation-circle" />
							<span>Unable to complete this request</span>
						</div>
						<p className="ai-message__error-text">{getAiMessageErrorText(message)}</p>
						{message.metadata?.stage ? (
							<div className="ai-message__error-stage">Stage: {String(message.metadata.stage)}</div>
						) : null}
					</div>
				) : (
					<AiMessageContent content={message.content} />
				)}
				{!isAdmin && !isError && message.totalTokens ? (
					<div className="ai-message__tokens">{message.totalTokens.toLocaleString()} tokens</div>
				) : null}
			</div>
		</div>
	);
};

export default AiMessageItem;
